import {AlertTriangle, Info, OctagonAlert} from 'lucide-react';
import {cn} from '@/lib/utils/cn';
import {CollapsibleSection, revealFinding} from './CollapsibleSection';
import type {ValidationFinding} from './lib/validator';

interface ValidationSummaryProps {
  findings: ValidationFinding[];
  activeGroupIndex: number;
  activeTransactionIndex: number;
}

/** A finding belongs on this summary when it's about the interchange as a
 * whole (ISA/IEA), the active transaction's enclosing group (GS/GE), or the
 * active transaction itself — everything else belongs to a different
 * transaction the user isn't currently looking at. */
function isRelevant(
  finding: ValidationFinding,
  groupIndex: number,
  transactionIndex: number
): boolean {
  if (finding.path === 'isaSegment' || finding.path === 'ieaSegment')
    return true;

  const groupPrefix = `groups[${groupIndex}].`;
  if (!finding.path.startsWith(groupPrefix)) return false;

  const rest = finding.path.slice(groupPrefix.length);
  if (rest.startsWith('gsSegment') || rest.startsWith('geSegment')) return true;
  return rest.startsWith(`transactions[${transactionIndex}]`);
}

function FindingRow({finding}: {finding: ValidationFinding}) {
  const isError = finding.severity === 'error';
  const Icon = isError ? OctagonAlert : AlertTriangle;
  // Element-level findings always sit on a rendered segment, so they can be
  // jumped to; envelope/missing-segment findings have nothing to jump to.
  const hasField = /\.elements\[\d+\]$/.test(finding.path);
  const className = cn(
    'flex w-full items-start gap-1.5 text-left text-sm',
    isError ? 'text-destructive' : 'text-warning'
  );
  const content = (
    <>
      <Icon className="mt-0.5 h-3.5 w-3.5 shrink-0" />
      <span className={cn(hasField && 'underline-offset-2 hover:underline')}>
        {finding.message}
      </span>
    </>
  );

  return hasField ? (
    <button
      type="button"
      className={className}
      onClick={() => revealFinding(finding.path)}
    >
      {content}
    </button>
  ) : (
    <p className={className}>{content}</p>
  );
}

/**
 * The advisory validation panel for the active transaction. Collapsed by
 * default like every other section, but its header already shows the error
 * and warning counts. Clicking a finding opens and jumps to the field it's
 * about. Deliberately, nothing here disables or gates anything.
 */
export function ValidationSummary({
  findings,
  activeGroupIndex,
  activeTransactionIndex,
}: ValidationSummaryProps) {
  const relevant = findings.filter(f =>
    isRelevant(f, activeGroupIndex, activeTransactionIndex)
  );
  const errors = relevant.filter(f => f.severity === 'error');
  const warnings = relevant.filter(f => f.severity === 'warning');

  return (
    <CollapsibleSection
      title="Validation"
      preview={
        relevant.length === 0
          ? 'No issues found'
          : 'Click an issue to jump to it'
      }
      issues={{errors: errors.length, warnings: warnings.length}}
    >
      <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
        <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        Advisory only — nothing here ever blocks or disables Export.
      </p>

      {relevant.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No issues found for this transaction.
        </p>
      ) : (
        <div className="space-y-1.5">
          {[...errors, ...warnings].map((finding, i) => (
            <FindingRow key={i} finding={finding} />
          ))}
        </div>
      )}
    </CollapsibleSection>
  );
}
